import {
  EXAM_COMPLETED_THRESHOLD,
  getExamResultLevel,
  isExamCompleted,
} from '../../utils/examResult'

const BRAND = '#01A76A'

const BookIcon = (props) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 19.5A2.5 2.5 0 016.5 17H20M4 19.5A2.5 2.5 0 006.5 22H20V4a2 2 0 00-2-2H6.5A2.5 2.5 0 004 4.5v15z" />
  </svg>
)

const CheckIcon = (props) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
)

const CrossIcon = (props) => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
  </svg>
)

// Progreso de exámenes de cursos (una tarjeta por curso, con la nota final y
// el detalle de cada pregunta respondida). Se usa en la Academia (talento) y
// en el seguimiento del master.
const ExamProgress = ({ progress = [], emptyText }) => {
  const entries = Array.isArray(progress) ? progress : []

  if (entries.length === 0) {
    return (
      <p className="text-on-bg-secondary mb-0" style={{ opacity: 0.75, fontSize: 14 }}>
        {emptyText || 'Todavía no hay respuestas registradas.'}
      </p>
    )
  }

  return (
    <div className="ep-list">
      {entries.map((entry, index) => {
        const grade = Math.round(Number(entry?.finalGrade) || 0)
        const level = getExamResultLevel(grade)
        const completed = isExamCompleted(entry)
        const questions = Array.isArray(entry?.questions) ? entry.questions : []
        const correctCount = questions.filter(
          (q) => Math.round(Number(q?.qualification) || 0) >= EXAM_COMPLETED_THRESHOLD
        ).length

        return (
          <div
            key={entry?.courseId ?? index}
            className="ep-course"
            style={{ '--ep-accent': level.color, animationDelay: `${index * 60}ms` }}
          >
            {/* Encabezado del curso */}
            <div className="ep-course-head">
              <div className="ep-course-title">
                <span className="ep-course-icon">
                  <BookIcon />
                </span>
                <div className="min-width-0">
                  <p className="ep-course-name mb-0">
                    {entry?.courseTitle || `Curso #${entry?.courseId}`}
                  </p>
                  <p className="ep-course-sub mb-0">
                    {questions.length > 0
                      ? `${correctCount}/${questions.length} preguntas correctas`
                      : 'Sin detalle de preguntas'}
                    {completed && (
                      <span className="ep-course-sub-check">
                        <CheckIcon width="9" height="9" />
                        Completado
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="ep-ring" style={{ '--ep-pct': `${grade * 3.6}deg` }}>
                <div className="ep-ring-inner">
                  <span>{grade}%</span>
                </div>
              </div>
            </div>

            {/* Detalle por pregunta */}
            {questions.length > 0 && (
              <div className="ep-questions">
                {questions.map((question, qIndex) => {
                  const qualification = Math.round(
                    Number(question?.qualification) || 0
                  )
                  const isCorrect = qualification >= EXAM_COMPLETED_THRESHOLD

                  return (
                    <div
                      key={question?.questionId ?? qIndex}
                      className={`ep-question-row ${!isCorrect ? 'incorrect' : ''}`}
                    >
                      <span
                        className={`ep-question-icon ${
                          isCorrect ? 'correct' : 'incorrect'
                        }`}
                      >
                        {isCorrect ? <CheckIcon /> : <CrossIcon />}
                      </span>
                      <p className="ep-question-text">
                        {question?.question || `Pregunta #${qIndex + 1}`}
                      </p>
                      <span
                        className={`ep-question-pct ${
                          isCorrect ? 'correct' : 'incorrect'
                        }`}
                      >
                        {qualification}%
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      <style>{`
        .ep-list {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .ep-course {
          position: relative;
          border-radius: 16px;
          border: 1px solid #eceff1;
          border-left: 3px solid var(--ep-accent);
          background: #fff;
          padding: 16px 18px;
          box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          animation: epFadeIn 0.4s ease both;
        }

        @media (hover: hover) and (pointer: fine) {
          .ep-course:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 24px -6px rgba(15, 23, 42, 0.1);
          }
        }

        @keyframes epFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .ep-course-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
        }

        .ep-course-title {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          min-width: 0;
        }

        .min-width-0 {
          min-width: 0;
        }

        .ep-course-name {
          font-weight: 700;
          font-size: 14.5px;
          color: #0f172a;
          line-height: 1.35;
        }

        .ep-course-sub {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 6px;
          font-size: 12px;
          color: #8a93a3;
          margin-top: 2px;
        }

        .ep-course-sub-check {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-weight: 700;
          color: ${BRAND};
        }

        .ep-course-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          flex-shrink: 0;
          margin-top: 1px;
          border-radius: 8px;
          background: rgba(1, 167, 106, 0.1);
        }

        .ep-ring {
          flex-shrink: 0;
          width: 46px;
          height: 46px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: conic-gradient(var(--ep-accent) var(--ep-pct), #eef1f0 0);
        }

        .ep-ring-inner {
          width: 37px;
          height: 37px;
          border-radius: 50%;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.05);
        }

        .ep-ring-inner span {
          font-size: 10.5px;
          font-weight: 800;
          color: #0f172a;
        }

        .ep-questions {
          margin-top: 14px;
          padding-top: 12px;
          border-top: 1px dashed #eef1f0;
          display: flex;
          flex-direction: column;
        }

        .ep-question-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 7px 4px;
          border-radius: 8px;
          transition: background 0.2s ease;
        }

        .ep-question-row.incorrect {
          background: rgba(239, 68, 68, 0.04);
        }

        .ep-question-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          border-radius: 50%;
        }

        .ep-question-icon.correct {
          background: rgba(1, 167, 106, 0.12);
          color: ${BRAND};
        }

        .ep-question-icon.incorrect {
          background: rgba(239, 68, 68, 0.12);
          color: #dc2626;
        }

        .ep-question-text {
          flex: 1 1 auto;
          min-width: 160px;
          margin: 0;
          font-size: 13px;
          color: #4b5563;
        }

        .ep-question-pct {
          flex-shrink: 0;
          font-size: 11.5px;
          font-weight: 700;
          color: #9ca3af;
        }

        .ep-question-pct.incorrect {
          color: #dc2626;
        }

        @media (max-width: 480px) {
          .ep-course-head {
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  )
}

export default ExamProgress
